/**
 * Submitting a Resource Plan to Resourcing (workspace feat-staffing-plan-status-workflow,
 * 2026-10-28). Pure, so the plan header and the server apply the same rule.
 *
 * Whoever can edit the plan (`manageResourcePlan`) sends it to Resourcing: its Resourcing
 * status moves from Not set, or Rejected, to In Review. The sales status is untouched — a
 * plan can be Open, Proposed or Won when it goes, as long as it is still live.
 *
 * A plan needs a NOS Deal before it goes. Resourcing staffs against the Deal, and a plan
 * cloned as an Option or Change Order starts without one.
 */

import { planStatusLabel } from "@/lib/plan-status-labels";

/** Sales statuses a plan can no longer be sent to Resourcing from. */
export const PLAN_STATUSES_CLOSED_TO_RESOURCING: readonly string[] = [
  "Lost",
  "Closed",
  "Superseded",
  "Locked",
];

export interface PlanSubmissionState {
  status: string;
  /** Null (or absent, on an older payload) is Not set. */
  resourcingStatus?: string | null;
}

/**
 * Whether the plan is in a state that can be submitted: nobody has submitted it yet (Not
 * set) or Resourcing rejected it, and it is still live. Says nothing about the Deal —
 * `submitToResourcingBlock` adds that, so the button can show and explain why it won't go.
 */
export function canSubmitToResourcing(plan: PlanSubmissionState): boolean {
  const resourcing = plan.resourcingStatus ?? null;
  return (
    (resourcing === null || resourcing === "Returned") &&
    !PLAN_STATUSES_CLOSED_TO_RESOURCING.includes(plan.status)
  );
}

export const SUBMIT_NEEDS_DEAL_MESSAGE =
  "Link this plan to a NOS Deal before submitting it to Resourcing.";

/** Why this plan can't be submitted right now, in the words the user reads; null if it can. */
export function submitToResourcingBlock(
  plan: PlanSubmissionState & { nymblDealId: string | null },
): string | null {
  if (PLAN_STATUSES_CLOSED_TO_RESOURCING.includes(plan.status)) {
    return `This plan is ${planStatusLabel(plan.status)}, so it can't be submitted to Resourcing.`;
  }
  const resourcing = plan.resourcingStatus ?? null;
  if (resourcing !== null && resourcing !== "Returned") {
    return "This plan is already with Resourcing.";
  }
  if (plan.nymblDealId === null) return SUBMIT_NEEDS_DEAL_MESSAGE;
  return null;
}
